// An always-editable table cell that saves itself.
//
// 🔴 ONE IMPLEMENTATION, TWO CALLERS, ON PURPOSE. The pricing grid and the
// charges table both needed "type in the cell and it saves", and two separate
// copies of commit-on-blur would drift — one would grow a guard the other
// lacked, and the money rules below are exactly the kind that go missing in a
// copy. So this lives here and both import it.
//
// CEO 2026-08-18, on tabs and edit buttons: "rather than going tab by tab and
// clicking an 'edit' button every time a pricing change is needed", then on
// charges: "You need to be able to update the setup and run charges - you
// can't edit it now." Charges WERE editable — behind an unlabelled 14px pencil
// at the end of the row, which is indistinguishable from not editable.
import { useEffect, useId, useState } from "react";
import { cn } from "@/lib/utils";

export function LiveCell(props: {
  /** The server's current value. The box follows this whenever it changes. */
  value: string;
  ariaLabel: string;
  align?: "left" | "right";
  disabled?: boolean;
  /** Return an error string to REFUSE the edit, or null to accept it. */
  validate: (raw: string) => string | null;
  /** Called only with a value that is both changed and valid. */
  onCommit: (raw: string) => void;
  className?: string;
}) {
  const {
    value,
    ariaLabel,
    align = "right",
    disabled,
    validate,
    onCommit,
    className,
  } = props;
  const [draft, setDraft] = useState(value);
  // 🔴 THE REASON, NOT JUST A FLAG. This was `bad: boolean` until 2026-08-20:
  // `validate()` computed a careful message — "Cost must be numeric (e.g. 12.00
  // or 1.692)", "Quantity must be a non-negative whole number" — and the cell
  // THREW IT AWAY, snapping the value back behind a red border with no
  // explanation. A rep who types `12,00` concludes the field is broken, which
  // is the same complaint the CEO made about the unlabelled pencil: an
  // affordance that gives no feedback reads as not working.
  const [refusal, setRefusal] = useState<string | null>(null);
  const errorId = useId();

  // The server is the source of truth. When it answers with a different value —
  // a re-derived margin, a rejected write, another rep's edit arriving on a
  // refetch — the box follows it rather than sitting on a stale draft.
  useEffect(() => {
    setDraft(value);
    setRefusal(null);
  }, [value]);

  // 🔒 The message is ABSOLUTELY POSITIONED. These cells live in a dense
  // pricing grid — one column per quantity break — and a message that took
  // layout space would shove every other column sideways the moment a rep
  // fat-fingered one cell. `relative` on the wrapper, `absolute` on the note:
  // the row height never changes. `whitespace-nowrap` + a high z-index keep it
  // readable over the neighbouring cell rather than wrapping into two lines.
  return (
    <span className="relative block">
    <input
      type="text"
      aria-label={ariaLabel}
      aria-invalid={refusal !== null}
      {...(refusal !== null ? { "aria-errormessage": errorId } : {})}
      disabled={disabled === true}
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        // Clear on the first keystroke: a stale message pinned under a field
        // the rep has already corrected is its own kind of wrong.
        if (refusal !== null) setRefusal(null);
      }}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={() => {
        const raw = draft.trim();
        // 🔴 UNCHANGED WRITES NOTHING. Tabbing across a grid to read it must not
        // PATCH every cell it passes through.
        if (raw === value.trim()) {
          setRefusal(null);
          return;
        }
        const err = validate(raw);
        if (err !== null) {
          // Refuse and revert: a rep must never be left looking at a number the
          // database does not hold — but TELL THEM WHY.
          setRefusal(err);
          setDraft(value);
          return;
        }
        setRefusal(null);
        onCommit(raw);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setDraft(value);
          e.currentTarget.blur();
        }
      }}
      className={cn(
        "w-full rounded-md border bg-white px-2 py-1.5 text-xs tabular-nums text-brown-500",
        "focus:border-peach-400 focus:outline-none focus:ring-1 focus:ring-peach-400",
        align === "right" ? "text-right" : "text-left",
        refusal !== null ? "border-rust-400 bg-rust-50" : "border-brown-100",
        disabled === true && "cursor-not-allowed bg-cream-50 text-brown-300",
        className,
      )}
    />
      {refusal !== null && (
        <span
          id={errorId}
          role="alert"
          className="absolute left-0 top-full z-20 mt-0.5 whitespace-nowrap rounded border border-rust-200 bg-rust-50 px-1.5 py-0.5 text-[11px] leading-tight text-rust-500 shadow-sm"
        >
          {refusal}
        </span>
      )}
    </span>
  );
}
